/**
 * API client — all requests to the FastAPI backend.
 * Base URL from VITE_API_BASE env var (default: http://localhost:8000).
 * See .env.example for configuration.
 */

import { getInitDataRaw } from "@/tg/auth";

// VITE_API_BASE should be set in .env; see .env.example
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BASE: string = (import.meta as any).env?.["VITE_API_BASE"] ?? "http://localhost:8000";

export { BASE };

// ─── In-memory cache (stale-while-revalidate; survives tab switches) ───────────
const _cache = new Map<string, unknown>();
function _peek<T>(key: string): T | undefined {
  return _cache.get(key) as T | undefined;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AuthResult {
  lang: "uk" | "en";
  is_member: boolean;
  is_admin: boolean;
}

export interface SectionItem {
  id: string;
  kind: string;
  title: string;
  subtitle: string | null;
  image: string | null;
  date: string | null;
  category: string | null;
}

export interface DetailItem {
  id: string;
  kind: string;
  title: string;
  image: string | null;
  paragraphs: string[];
  link: string | null;
  date: string | null;
}

export interface AboutData {
  hotline: string;
  emails: string[];
  socials: Record<string, string>;
  disclaimer: string;
  official_site: string;
  official_channel: string;
}

export interface AdminItemBody {
  kind: string;
  locale: string;
  title: string;
  subtitle?: string | undefined;
  paragraphs?: string[] | undefined;
  image_url?: string | undefined;
  link?: string | undefined;
  date?: string | undefined;
  sort?: number | undefined;
  published?: boolean | undefined;
}

export interface AdminItem {
  id: string;
  kind: string;
  locale: string;
  title: string;
  subtitle?: string | undefined;
  paragraphs?: string[] | undefined;
  image_url?: string | undefined;
  link?: string | undefined;
  date?: string | undefined;
  sort?: number | undefined;
  published?: boolean | undefined;
}

// ─── Typed error ──────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ─── Core fetch helpers ───────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const raw = getInitDataRaw();
  return raw ? { Authorization: `tma ${raw}` } : {};
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const MAX_ATTEMPTS = 3;
  const RETRY_DELAY_MS = 1200;
  const headers = {
    "Content-Type": "application/json",
    ...authHeaders(),
    ...(options.headers as Record<string, string> | undefined),
  };

  let lastError: unknown;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${BASE}${path}`, { ...options, headers });
    } catch (networkErr) {
      // Network failure — retry on transient blip
      lastError = networkErr;
      if (attempt < MAX_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        continue;
      }
      throw networkErr;
    }

    if (!res.ok) {
      // Retry only 5xx (cold-start / server blip); throw immediately on 4xx
      if (res.status >= 500 && attempt < MAX_ATTEMPTS) {
        lastError = res;
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        continue;
      }
      let msg = `${res.status} ${res.statusText}`;
      try {
        const body = (await res.json()) as { detail?: string };
        if (body.detail) msg = body.detail;
      } catch {
        // ignore parse error
      }
      throw new ApiError(res.status, msg);
    }

    return res.json() as Promise<T>;
  }

  // Exhausted retries on network error path
  throw lastError;
}

// ─── Public API calls ─────────────────────────────────────────────────────────

/** POST /api/auth — verify initData, get user profile. */
export async function auth(): Promise<AuthResult> {
  return apiFetch<AuthResult>("/api/auth", { method: "POST" });
}

/** GET /api/{kind}?lang= — list items in a section. */
export async function getSection(kind: string, lang: string): Promise<SectionItem[]> {
  const data = await apiFetch<SectionItem[]>(`/api/${kind}?lang=${encodeURIComponent(lang)}`);
  _cache.set(`section:${kind}:${lang}`, data);
  return data;
}

/** Synchronous peek at the cached section list (undefined if never fetched). */
export function peekSection(kind: string, lang: string): SectionItem[] | undefined {
  return _peek<SectionItem[]>(`section:${kind}:${lang}`);
}

/** GET /api/items/{kind}/{id}?lang= — single item detail. */
export async function getItem(kind: string, id: string, lang: string): Promise<DetailItem> {
  return apiFetch<DetailItem>(`/api/items/${kind}/${id}?lang=${encodeURIComponent(lang)}`);
}

/** GET /api/about?lang= — about/contact data. */
export async function getAbout(lang: string): Promise<AboutData> {
  return apiFetch<AboutData>(`/api/about?lang=${encodeURIComponent(lang)}`);
}

// ─── Admin API ────────────────────────────────────────────────────────────────

/** GET /api/admin/items — list all managed items. */
export async function adminList(kind?: string, locale?: string): Promise<AdminItem[]> {
  const params = new URLSearchParams();
  if (kind) params.set("kind", kind);
  if (locale) params.set("locale", locale);
  const qs = params.toString();
  return apiFetch<AdminItem[]>(`/api/admin/items${qs ? `?${qs}` : ""}`);
}

/** POST /api/admin/items — create a new item. */
export async function adminCreate(body: AdminItemBody): Promise<AdminItem> {
  return apiFetch<AdminItem>("/api/admin/items", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** PATCH /api/admin/items/{id} — update existing item. */
export async function adminUpdate(id: string, body: Partial<AdminItemBody>): Promise<AdminItem> {
  return apiFetch<AdminItem>(`/api/admin/items/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** DELETE /api/admin/items/{id} — delete item. */
export async function adminDelete(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/admin/items/${id}`, { method: "DELETE" });
}

// ─── Membership ───────────────────────────────────────────────────────────────

export interface Membership {
  donated_total: number;
  monthly: number;
  level: number;
  next_level: number | null;
  next_threshold: number | null;
  points: number;
  currency: string;
}

/** GET /api/me?lang= — demo membership for the member hub. */
export async function getMe(lang: string): Promise<Membership> {
  const data = await apiFetch<Membership>(`/api/me?lang=${encodeURIComponent(lang)}`);
  _cache.set(`me:${lang}`, data);
  return data;
}

/** Synchronous peek at the cached membership (undefined if never fetched). */
export function peekMe(lang: string): Membership | undefined {
  return _peek<Membership>(`me:${lang}`);
}
